import '../../styles/restScreens.css';
import React from 'react';
import { WIDGET_TYPES } from './widgetRegistry';
import KPICardWidget from './KPICardWidget';
import RevenueChartWidget from './RevenueChartWidget';
import QuickActionsWidget from './QuickActionsWidget';
import RecentActivityWidget from './RecentActivityWidget';
import StackableTilesWidget from './StackableTilesWidget';
import FormulaBuilderWidget from './FormulaBuilderWidget';
import GroupWidget from './GroupWidget';

/**
 * WidgetFactory Component
 *
 * Factory component that renders the appropriate widget based on its type
 */
const WidgetFactory = ({
  widget,
  onRemove,
  onConfigure,
  isEditing,
  onAction,
  onUngroup,
  onRemoveFromGroup,
  theme,
  metrics,
  isUser,
  areaAccess
}) => {
  const { id, type, config } = widget;

  const widgetProps = {
    id,
    config,
    onRemove,
    onConfigure,
    isEditing,
    onAction,
    theme,
    // BL-194 Gate 1: the live aggregates from the Supabase-direct data layer.
    // Every data-backed tile reads from this one fetch instead of inventing its
    // own numbers.
    metrics,
    isUser,
    areaAccess
  };

  switch (type) {
    case WIDGET_TYPES.KPI_CARD:
      return <KPICardWidget {...widgetProps} />;

    case WIDGET_TYPES.REVENUE_CHART:
      return <RevenueChartWidget {...widgetProps} />;

    case WIDGET_TYPES.CLIENT_CHART:
      // For now, reuse RevenueChartWidget with different config
      return <RevenueChartWidget {...widgetProps} />;

    case WIDGET_TYPES.RECENT_ACTIVITY:
      return <RecentActivityWidget {...widgetProps} />;

    case WIDGET_TYPES.QUICK_ACTIONS:
      return <QuickActionsWidget {...widgetProps} />;

    case WIDGET_TYPES.CALENDAR_PREVIEW:
      // Placeholder for calendar preview widget
      return (
        <div data-m6-layout="widgets" data-m6-component="WidgetFactory" className="rounded-lg shadow-md p-4 h-full flex items-center justify-center bg-[var(--surface-raised)]">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-2 text-[var(--ink-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-[var(--ink-muted)]">Calendar Preview</p>
            <p className="text-xs mt-1 text-[var(--ink-muted)]">Coming soon</p>
          </div>
        </div>
      );

    case WIDGET_TYPES.FINANCIAL_SUMMARY:
      // Placeholder for financial summary widget
      return (
        <div data-m6-layout="widgets" data-m6-component="WidgetFactory" className="rounded-lg shadow-md p-4 h-full flex items-center justify-center bg-[var(--surface-raised)]">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-2 text-[var(--ink-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-[var(--ink-muted)]">Financial Summary</p>
            <p className="text-xs mt-1 text-[var(--ink-muted)]">Coming soon</p>
          </div>
        </div>
      );

    case WIDGET_TYPES.STACKABLE_TILES:
      return <StackableTilesWidget widget={widget} {...widgetProps} />;

    case WIDGET_TYPES.FORMULA_BUILDER:
      return <FormulaBuilderWidget widget={widget} {...widgetProps} />;

    case WIDGET_TYPES.GROUP:
      return (
        <GroupWidget
          widget={widget}
          {...widgetProps}
          onUngroup={onUngroup}
          onRemoveFromGroup={onRemoveFromGroup}
        />
      );

    case WIDGET_TYPES.CLIENT_PIPELINE:
    case WIDGET_TYPES.EXPENSE_CHART:
    case WIDGET_TYPES.PROFIT_LOSS:
    case WIDGET_TYPES.TASKS_REMINDERS:
      // Placeholders for other widget types
      return (
        <div data-m6-layout="widgets" data-m6-component="WidgetFactory" className="bg-[var(--surface-raised)] rounded-lg shadow-md p-4 h-full flex items-center justify-center">
          <div className="text-center">
            <svg className="w-12 h-12 text-[var(--ink-muted)] mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            <p className="text-sm text-[var(--ink-muted)]">{type}</p>
            <p className="text-xs text-[var(--ink-muted)] mt-1">Coming soon</p>
          </div>
        </div>
      );

    default:
      // Fallback for unknown widget types
      return (
        <div data-m6-layout="widgets" data-m6-component="WidgetFactory" className="bg-[var(--surface-raised)] rounded-lg shadow-md p-4 h-full flex items-center justify-center">
          <div className="text-center">
            <svg className="w-12 h-12 text-[var(--ink-muted)] mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-[var(--ink-muted)]">Unknown widget type</p>
            <p className="text-xs text-[var(--ink-muted)] mt-1">{type}</p>
          </div>
        </div>
      );
  }
};

export default WidgetFactory;
